/**
 * Booking flow RULES shared by the web sheet/sidebar (`@majourneys/booking`)
 * and the mobile sheet (`apps/mobile/src/features/bookings`).
 *
 * Both platforms draw their own UI; nothing in here is visual. What lives here
 * is the set of decisions the two had each written separately and got wrong in
 * different ways during the week of 2026-09-10:
 *
 *  - which times a venue offers (three copies of the slot builder, three
 *    different answers for Buggy à Fès, whose session times live on the
 *    ACTIVITY and not on the package);
 *  - which steps a flow has, and which it skips;
 *  - what a table reservation must NOT send to the server (a package id would
 *    bill a free table);
 *  - when the pickup toggle is offered, withheld, or free.
 *
 * Every function is pure and platform-free. Where the two platforms genuinely
 * still disagree the difference is an explicit option, named and documented,
 * rather than a second implementation — so the divergence is visible in one
 * place instead of hiding in 3,800 lines of component.
 */

import { filterFutureTimes, DEFAULT_RESTAURANT_TIME_SLOTS } from "./booking-time";
import { restaurantSeatingsOn } from "./restaurant-seatings";
import {
  hasLargeTransportTier,
  hasOutsideSurcharge,
  isTransportAvailable,
  isTransportOffered,
  resolveTransportPrice,
  LARGE_GROUP_MAX_GUESTS,
} from "../pricing";

/* -------------------------------------------------------------------------- */
/* Time slots                                                                  */
/* -------------------------------------------------------------------------- */

/** Default daytime window when the operator set no `timeStart` / `timeEnd`. */
export const DEFAULT_SLOT_START_MIN = 9 * 60;
/** Default daytime window when the operator set no `timeStart` / `timeEnd`. */
export const DEFAULT_SLOT_END_MIN = 18 * 60;

/** End of the day, as a closing time. See `buildPackageWindowSlots`. */
const MIDNIGHT_MIN = 24 * 60;

/** The bits of a package this module reads. Both platforms' package types fit. */
export interface PackageTimeWindow {
  timeSlots?: readonly string[] | null;
  timeStart?: string | null;
  timeEnd?: string | null;
  /**
   * The window is ONE departure at an hour the team confirms later, not hourly
   * slots. See `flexibleWindow`.
   */
  flexibleTime?: boolean | null;
}

/**
 * The window of a package whose time is flexible, as `"HH:MM"` start and end,
 * or `null` when the package books exact times. Paragliding in Rabat leaves
 * between 13:00 and 15:00 depending on the wind: the guest picks the one
 * "13:00–15:00" choice, the booking stores `start` as its time and `end` as its
 * time end. Exact departures (`timeSlots`) win, as they do everywhere else.
 */
export function flexibleWindow(
  pkg: PackageTimeWindow | null | undefined,
): { start: string; end: string } | null {
  if (!pkg?.flexibleTime || packageFixedSlots(pkg).length > 0) return null;
  const start = parseTimeToMinutes(pkg.timeStart);
  const end = parseTimeToMinutes(pkg.timeEnd);
  if (start === null || end === null || end <= start) return null;
  return { start: formatMinutesAsTime(start), end: formatMinutesAsTime(end) };
}

/**
 * End of the window a booking at `time` was made in, or `null` for an exact
 * time. Only while `time` still IS the window's start: a booking the team moved
 * to 14:00 reads 14:00.
 */
export function bookedTimeEnd(
  time: string | null | undefined,
  pkg: PackageTimeWindow | null | undefined,
): string | null {
  const win = flexibleWindow(pkg);
  return win && time && time.slice(0, 5) === win.start ? win.end : null;
}

/** `"13:00"` + `"15:00"` → `"13:00–15:00"`; no end → the time as it is. */
export function formatTimeRange(time: string, end?: string | null): string {
  const start = time.slice(0, 5);
  return end ? `${start}–${end.slice(0, 5)}` : start;
}

/**
 * The label for one time choice: the range when `slot` opens the package's
 * flexible window, the slot itself otherwise.
 */
export function timeSlotLabel(slot: string, pkg: PackageTimeWindow | null | undefined): string {
  const win = flexibleWindow(pkg);
  return win && slot.slice(0, 5) === win.start ? formatTimeRange(win.start, win.end) : slot.slice(0, 5);
}

/** `"14:30"`, `"14:30:00"` and `"9:05"` → minutes past midnight. Else `null`. */
export function parseTimeToMinutes(value?: string | null): number | null {
  if (!value) return null;
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

/** Minutes past midnight → `"HH:MM"`. */
export function formatMinutesAsTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Exact departures declared on the package, normalised to `"HH:MM"`. A window
 * can only be walked in one-hour steps, so a package that leaves at 14:00 and
 * 16:00 and nothing between needs its own list.
 */
export function packageFixedSlots(pkg: PackageTimeWindow | null | undefined): string[] {
  return (pkg?.timeSlots ?? [])
    .filter((slot): slot is string => typeof slot === "string" && slot.length > 0)
    .map((slot) => slot.slice(0, 5));
}

/**
 * What to do when a package declares no opening window of its own. The three
 * surfaces answered this differently and each answer is load-bearing:
 *
 * - `"full"` — assume 09:00–18:00 whichever end is missing. The web sheet does
 *   this: it has no server fallback to lean on, so a slotless package would
 *   otherwise offer no time at all.
 * - `"partial"` — a package with NEITHER end offers nothing (the sidebar then
 *   asks the server, which is the only place an activity's fixed 15:30-style
 *   session times were known); one missing end is filled from the default.
 * - `"none"` — either end missing yields nothing. What mobile did, and half of
 *   why Buggy à Fès looked fully booked on every date.
 */
export type PackageWindowFallback = "full" | "partial" | "none";

/**
 * Hourly slots from a package's own window, or its exact departure list when it
 * has one. Never consults the activity — see `buildTimeSlots` for precedence.
 */
export function buildPackageWindowSlots(
  pkg: PackageTimeWindow | null | undefined,
  fallback: PackageWindowFallback = "none",
): string[] {
  if (!pkg) return [];
  const fixed = packageFixedSlots(pkg);
  if (fixed.length > 0) return fixed;
  // One departure, hour to be confirmed: a single choice, stored as the start.
  const flexible = flexibleWindow(pkg);
  if (flexible) return [flexible.start];

  const rawStart = parseTimeToMinutes(pkg.timeStart);
  let rawEnd = parseTimeToMinutes(pkg.timeEnd);

  // "Closes at 00:00" means midnight, not minute zero of the same morning.
  // Marrakech spas and rooftops are entered that way — Marajah Spa is open
  // 09:00–00:00 — and read literally the window is inverted, so every hour of
  // the day was dropped and the sidebar offered nothing. A package saved
  // 00:00–00:00 declares no window at all rather than a lone midnight slot;
  // that exact row (Marajah's "Tonic - 1h") was the only time on offer.
  if (rawStart === 0 && rawEnd === 0) return [];
  if (rawEnd === 0 && rawStart !== null && rawStart > 0) rawEnd = MIDNIGHT_MIN;

  if (fallback === "none" && (rawStart === null || rawEnd === null)) return [];
  if (fallback === "partial" && rawStart === null && rawEnd === null) return [];

  const start = rawStart ?? DEFAULT_SLOT_START_MIN;
  const end = rawEnd ?? DEFAULT_SLOT_END_MIN;
  if (end < start) return [];

  // `<` not `<=` on midnight: there is no 24:00 to book, and 00:00 would read
  // as the small hours of the day before.
  const out: string[] = [];
  for (let t = start; t <= end && t < MIDNIGHT_MIN; t += 60) out.push(formatMinutesAsTime(t));
  return out;
}

export interface TimeSlotSources {
  /**
   * The ACTIVITY's own fixed session times (`activities.time_slots`).
   * Appointment-based venues use these instead of an hourly grid, and they are
   * the only way to express a half-hour start like 15:30 — so they outrank the
   * package's window. Buggy à Fès carries six of them and a package with
   * `timeStart: null, timeEnd: null`; mobile read only the package and told
   * every guest the venue was fully booked on every date (owner, 2026-09-10).
   */
  activityTimeSlots?: readonly string[] | null;
  /**
   * A restaurant's seatings never come from a package window — a leftover
   * "dinner 19:30–02:30" package spans midnight and yields nothing at all.
   * Until the venue's own times are set, offer the shared default seatings.
   */
  isTableFlow?: boolean;
  /**
   * A restaurant's `opening_hours`, raw. With `date`, its seatings follow that
   * day's hours: opening until an hour before closing (`restaurantSeatingsOn`).
   * Unknown hours leave the seatings as they were.
   */
  openingHours?: unknown;
  /** The day being booked, `YYYY-MM-DD`. Only read with `openingHours`. */
  date?: string;
  /**
   * The restaurant's `tablePolicy.lastSeatingBeforeCloseMin`: how long before
   * closing its last table is taken. Omit for the default hour.
   */
  lastSeatingBeforeCloseMin?: number | null;
  pkg?: PackageTimeWindow | null;
  /** See `PackageWindowFallback`. Defaults to the strictest option. */
  windowFallback?: PackageWindowFallback;
}

/**
 * The times a venue offers, in precedence order:
 * (restaurant with opening hours) that day's seatings → activity session
 * times → (restaurant) default seatings → package fixed
 * departures → package hourly window → nothing.
 *
 * Unfiltered: still contains hours that may already have passed. Use
 * `resolveTimeSlots` to get the list a guest may actually pick from.
 */
export function buildTimeSlots(sources: TimeSlotSources): string[] {
  const fixed = (sources.activityTimeSlots ?? [])
    .filter((slot): slot is string => typeof slot === "string" && slot.length > 0)
    .map((slot) => slot.slice(0, 5));
  if (sources.isTableFlow && sources.date) {
    const seatings = restaurantSeatingsOn(
      sources.openingHours,
      sources.date,
      fixed,
      sources.lastSeatingBeforeCloseMin,
    );
    if (seatings !== null) return seatings;
  }
  if (fixed.length > 0) return fixed;
  if (sources.isTableFlow) return [...DEFAULT_RESTAURANT_TIME_SLOTS];
  return buildPackageWindowSlots(sources.pkg, sources.windowFallback ?? "none");
}

/**
 * `buildTimeSlots` with the hours that have already passed on `date` dropped
 * (Morocco time — the only clock the server compares against), so "today"
 * never offers, or auto-selects, an hour that is gone.
 */
export function resolveTimeSlots(
  sources: TimeSlotSources,
  date: string,
  now: Date = new Date(),
): string[] {
  return filterFutureTimes(buildTimeSlots(sources), date, now);
}

/* -------------------------------------------------------------------------- */
/* Time source — where a date's times actually come from                       */
/* -------------------------------------------------------------------------- */

/**
 * What the availability endpoint (`/activities/explore/:slug/times?date=`) said
 * about one date. Everything is optional so a caller with no request in flight
 * (no date picked yet) can pass `{}` and get the package-only answer.
 */
export interface ServerTimesState {
  /** The request is in flight. Nothing has been decided yet. */
  isLoading?: boolean;
  /** A stop sale closes the WHOLE day — not "we are busy", "we are shut". */
  blocked?: boolean;
  /** Times the server offers for the date, `"HH:MM"` (or `"HH:MM:SS"`). */
  times?: readonly string[] | null;
}

/** Which rung of the precedence ladder produced the times on offer. */
export type TimeSlotSource = "activity" | "table" | "package" | "server";

export type TimeSourceResolution =
  /** Times to draw. `source` says which rung won, for tests and telemetry. */
  | { kind: "slots"; source: TimeSlotSource; slots: string[] }
  /** Still asking the server. Draw a spinner — never "no availability". */
  | { kind: "loading" }
  /** Stop sale. The day is closed; another day will work. */
  | { kind: "blocked" }
  /** The venue declares hours, but every one of them has passed for this date. */
  | { kind: "past" }
  /** Nobody — package nor server — offers a time for this date. */
  | { kind: "none" };

export interface TimeSourceInput extends TimeSlotSources {
  /** The date being asked about, `YYYY-MM-DD`. */
  date: string;
  /** What the availability endpoint said. Omit when nothing was asked. */
  server?: ServerTimesState | null;
  /** Injectable clock, so "already passed" is testable. */
  now?: Date;
}

/**
 * The times a guest may pick for ONE date, and why there are none when there
 * are none.
 *
 * `buildTimeSlots` answers "what does this venue declare" from the catalogue
 * alone. That is not the whole answer: some activities declare nothing at all —
 * `sessionTimes: []` on the activity, `timeStart/timeEnd/timeSlots` all null on
 * the package — and their real times live only behind the availability
 * endpoint, which also knows live capacity. The web sidebar has always asked
 * it; mobile never did, so Off-Road E-Bike Palmeraie (eleven server slots,
 * nothing declared anywhere in the catalogue) told every guest "no availability"
 * on every date while the same activity booked fine on the website (owner,
 * 2026-09-10).
 *
 * The order, and why each step is where it is:
 *
 * 1. **`blocked` wins over everything.** A stop sale shuts the day regardless of
 *    what hours the package prints.
 * 2. **A package that declares its own hours keeps them.** Falling back to the
 *    server when a package DOES declare hours offered the generic hourly grid
 *    instead — a 09:00/10:00 tour was bookable at 17:00 all afternoon. So the
 *    server's list is for venues that declare none, not a superset to merge in.
 * 3. **Declared hours that have all passed are `"past"`, not `"none"`.** Same
 *    trap: "no times left today" must not fall through to the server's grid.
 * 4. **Only then the server's list**, and only while it is settled — a request
 *    in flight is `"loading"`, never an empty state.
 *
 * A failed request is indistinguishable here from one that returned nothing;
 * both leave `server.times` empty and yield `"none"`. Callers that want a blip
 * to stay harmless should treat the DAY as open on failure (as
 * `useDateAvailability` does for `blocked`) — the checkout refuses a bad slot
 * anyway, and a network hiccup must not lock a guest out of a date that is fine.
 */
export function resolveTimeSource(input: TimeSourceInput): TimeSourceResolution {
  const server = input.server ?? {};

  // 1. A stop sale closes the whole day, whatever the catalogue declares.
  if (server.blocked === true) return { kind: "blocked" };

  const declared = buildTimeSlots(input);
  // A restaurant whose hours say it is shut that day has nothing to ask the
  // server about: its answer would be the same empty day.
  if (
    declared.length === 0 &&
    input.isTableFlow &&
    restaurantSeatingsOn(input.openingHours, input.date) !== null
  ) {
    return { kind: "none" };
  }
  if (declared.length > 0) {
    const future = filterFutureTimes(declared, input.date, input.now);
    // 2 + 3. Declared hours are authoritative — including when they are spent.
    if (future.length === 0) return { kind: "past" };
    const source: TimeSlotSource =
      (input.activityTimeSlots ?? []).some((s) => typeof s === "string" && s.length > 0)
        ? "activity"
        : input.isTableFlow
          ? "table"
          : "package";
    return { kind: "slots", source, slots: future };
  }

  // 4. Nothing declared. The server is the only one who knows.
  if (server.isLoading === true) return { kind: "loading" };

  const serverTimes = (server.times ?? [])
    .filter((slot): slot is string => typeof slot === "string" && slot.length > 0)
    .map((slot) => slot.slice(0, 5));
  if (serverTimes.length === 0) return { kind: "none" };

  const future = filterFutureTimes(serverTimes, input.date, input.now);
  if (future.length === 0) return { kind: "past" };
  return { kind: "slots", source: "server", slots: future };
}

/* -------------------------------------------------------------------------- */
/* Nightclubs                                                                  */
/* -------------------------------------------------------------------------- */

/** The category a club is filed under when the catalogue is right. */
const NIGHTCLUB_CATEGORY_SLUGS: ReadonlySet<string> = new Set(["nightclubs", "nightclub"]);

/** Segments a club can be filed under. Explore rows use `nightlife`. */
const NIGHTLIFE_SEGMENTS: ReadonlySet<string> = new Set(["nightlife", "nightclubs"]);

/** Earliest a door can open and still be a night out rather than a dinner. */
const NIGHT_OPENS_AFTER_MIN = 21 * 60;
/** Latest a closing time can be and still belong to the night before. */
const NIGHT_CLOSES_BEFORE_MIN = 6 * 60;

function normalizeSlug(value?: string | null): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

/**
 * A window that opens late and closes after midnight — 23:00 to 05:00, not
 * 19:00 to 23:30. `end < start` is the wrap; the two bounds keep a restaurant
 * that serves until midnight out of it.
 */
function opensOvernight(pkg: PackageTimeWindow | null | undefined): boolean {
  const start = parseTimeToMinutes(pkg?.timeStart);
  const end = parseTimeToMinutes(pkg?.timeEnd);
  if (start === null || end === null) return false;
  return end < start && start >= NIGHT_OPENS_AFTER_MIN && end <= NIGHT_CLOSES_BEFORE_MIN;
}

export interface NightclubInput {
  categorySlug?: string | null;
  /** The activity's discover segment. */
  segment?: string | null;
  /** The activity's published packages, read for their opening window. */
  packages?: readonly PackageTimeWindow[] | null;
}

/**
 * Does this activity book a NIGHT rather than a time slot?
 *
 * The category is the answer whenever it is there. The rest is a safety net,
 * and it is not hypothetical: a database copy with Leone filed under no
 * category at all put the club through the ordinary flow, which read its
 * 300 MAD entry as an hourly rate and offered "1 to 8 hours" for a nightclub
 * (owner, 2026-09-17). Falling back to a generic flow is worse than failing
 * loudly, because nobody sees it until a guest is looking at it.
 *
 * The net is deliberately narrow: nightlife AND a door that opens at 21:00 or
 * later and closes after midnight. A rooftop bar that shuts at 23:30 does not
 * qualify, so it keeps its time slots.
 */
export function isNightclubBooking(input: NightclubInput): boolean {
  if (NIGHTCLUB_CATEGORY_SLUGS.has(normalizeSlug(input.categorySlug))) return true;
  if (!NIGHTLIFE_SEGMENTS.has(normalizeSlug(input.segment))) return false;
  return (input.packages ?? []).some(opensOvernight);
}

/* -------------------------------------------------------------------------- */
/* Flow shape                                                                  */
/* -------------------------------------------------------------------------- */

export interface BookingFlowShapeInput {
  /** Restaurant: holds a table, sells no package, so the package step is moot. */
  isTableFlow: boolean;
  /** How many packages the activity publishes. */
  packageCount: number;
  /**
   * Total steps the platform's sheet defines, INCLUDING the terminal success
   * step. Web has 5 (package · when · who · details · done); mobile has 6
   * (package · when · who · details · review · success).
   */
  totalSteps: number;
  /**
   * Skip the package step when there is only one package to choose. Both
   * sheets do for activities and spas (mobile since 2026-10-16, owner: "do the
   * same web booking flow for spas, activities on app"); the app leaves it off
   * for stays, clubs and transfers. Left as an option because a caller that
   * passes it must also open on step 1, or the step count reads "Step 0".
   */
  skipSinglePackageStep?: boolean;
}

export interface BookingFlowShape {
  /** The package step (index 0) is not part of this flow. */
  skipPackageStep: boolean;
  /** Index the flow opens on, and the floor the Back button closes below. */
  firstStep: number;
  /** How many steps the progress indicator counts (success step excluded). */
  visibleStepCount: number;
}

/** Which steps this booking actually has. */
export function bookingFlowShape(input: BookingFlowShapeInput): BookingFlowShape {
  const skipPackageStep =
    input.isTableFlow ||
    (input.skipSinglePackageStep === true && input.packageCount <= 1);
  const firstStep = skipPackageStep ? 1 : 0;
  return {
    skipPackageStep,
    firstStep,
    visibleStepCount: input.totalSteps - 1 - firstStep,
  };
}

/* -------------------------------------------------------------------------- */
/* Submission payload                                                          */
/* -------------------------------------------------------------------------- */

export interface BookingParty {
  adults: number;
  children: number;
  infants: number;
}

/**
 * The headcount a table reservation sends. Infants are never a separate band on
 * a table — a restaurant seats people, and we charge nothing either way.
 *
 * Adults and children are two counts that add up, on every surface (owner,
 * 2026-10-23). The web and app used to ask for one "Guests" figure with the
 * children inside it and split them back out here; guests read that figure as
 * adults, so "4 adults, 3 children" arrived as 1 adult + 3 children
 * (MA-1792778539514).
 */
export function tableFlowParty(input: BookingParty): BookingParty {
  return {
    adults: input.adults,
    children: input.children,
    infants: 0,
  };
}

export interface BookingSubmissionInput extends BookingParty {
  isTableFlow: boolean;
  packageId?: string | null;
  /** Price computed by the platform's own total calculator. */
  total: number;
  /** Pickup actually applied (already gated through `pickupState`). */
  transport: boolean;
  /** Outside-city surcharge actually applied. */
  transportOutside?: boolean;
}

export interface BookingSubmissionCore {
  packageId: string | undefined;
  adults: number;
  children: number;
  infants: number;
  transport: boolean;
  /** `undefined` on a table flow — there is no pickup to be outside of. */
  transportOutside: boolean | undefined;
  total: number;
}

/**
 * The parts of a checkout payload that a table reservation changes.
 *
 * Never send a package on a table flow: a restaurant can still carry a legacy
 * package row ("Dinner, 300 MAD") and the server prices whatever id it is
 * handed, so a free table would arrive billed. Same reason `total` is forced to
 * 0, infants to 0, and pickup off.
 */
export function bookingSubmissionCore(
  input: BookingSubmissionInput,
): BookingSubmissionCore {
  if (!input.isTableFlow) {
    return {
      packageId: input.packageId ?? undefined,
      adults: input.adults,
      children: input.children,
      infants: input.infants,
      transport: input.transport,
      transportOutside: input.transportOutside,
      total: input.total,
    };
  }
  const party = tableFlowParty(input);
  return {
    packageId: undefined,
    adults: party.adults,
    children: party.children,
    infants: 0,
    transport: false,
    transportOutside: undefined,
    total: 0,
  };
}

/* -------------------------------------------------------------------------- */
/* Pickup gating                                                               */
/* -------------------------------------------------------------------------- */

export interface PickupStateInput {
  /** Package `pricing.transport`. `null`/negative = not offered, 0 = free. */
  transport: number | null | undefined;
  /** Package `pricing.transportLarge` — the 8–12 guest tier, when tiered. */
  transportLarge?: number | null;
  /** Package `pricing.transportOutside` — flat surcharge beyond the city. */
  transportOutside?: number | null;
  /** Paying guests, which is what the tier is chosen by. */
  paidGuests: number;
  /** Guest is staying outside the activity's city. */
  outside?: boolean;
  /** Guest asked for pickup. */
  selected?: boolean;
  /**
   * Treat a free (0 MAD) pickup as "not offered".
   *
   * The web sheet has always done this, and it is why a genuinely free pickup
   * has no toggle there — the driver then gets no address. Mobile does not.
   * Kept as a flag so this refactor changes nothing; flipping web to `false`
   * is a one-line, product-visible decision for the owner.
   */
  requirePaidPickup?: boolean;
  /**
   * The pick-up would be less than `RIDE_NOTICE_MINUTES` away (see
   * `isRideTooSoon`). The activity can still be booked; the ride cannot, so
   * the add-on is withheld and a ticked one is not applied.
   */
  tooSoon?: boolean;
}

export interface PickupState {
  /** The package offers pickup at all, ignoring group size. */
  offered: boolean;
  /** Pickup can be chosen for this group size. */
  available: boolean;
  /** Offered, but this group is too large for a tiered venue's pickup. */
  withheld: boolean;
  /** Offered, but the pick-up would be under two hours away: say so, offer nothing. */
  tooSoon: boolean;
  /** Offered and available at no charge. */
  free: boolean;
  /** Resolved price including any outside-city surcharge. 0 when unavailable. */
  price: number;
  /** Guest chose pickup AND it is actually available. */
  applied: boolean;
  /** The outside-city surcharge rides on an applied pickup that prices one. */
  outsideApplied: boolean;
}

/**
 * Is the pickup toggle offered, and at what price?
 *
 * Wraps the tri-state rules in `../pricing` so both platforms read them the
 * same way. `isTransportOffered` treats the catalogue's legacy `-1` sentinel as
 * "not offered"; mobile used to draw "Hotel pickup — Included" on activities
 * whose own page says pickup is not offered, and web hid the toggle even when
 * pickup was genuinely free. Two wrong behaviours from one helper read twice.
 */
export function pickupState(input: PickupStateInput): PickupState {
  const {
    transport,
    transportLarge = null,
    transportOutside = null,
    paidGuests,
    outside = false,
    selected = false,
    requirePaidPickup = false,
    tooSoon = false,
  } = input;

  const price = resolveTransportPrice(transport, transportLarge, paidGuests, {
    outside,
    outsideSurcharge: transportOutside,
  });

  const offered = requirePaidPickup
    ? (transport ?? 0) > 0
    : isTransportOffered(transport);
  const bookable = requirePaidPickup
    ? offered && price > 0
    : isTransportAvailable(transport, transportLarge, paidGuests);
  const withheld = requirePaidPickup
    ? offered && !bookable
    : offered &&
      hasLargeTransportTier(transportLarge) &&
      paidGuests > LARGE_GROUP_MAX_GUESTS;
  // Too soon wins over nothing but "not offered": a group too large for the
  // pick-up hears about the group first, as it did before.
  const late = offered && !withheld && tooSoon;
  const available = bookable && !late;

  const applied = selected && available;
  return {
    offered,
    available,
    withheld,
    tooSoon: late,
    free: available && price <= 0,
    price,
    applied,
    outsideApplied: applied && outside && hasOutsideSurcharge(transportOutside),
  };
}
